import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { handleCreateRequest } from '../controllers/requestController';
import Turnstile, { captchaEnabled } from '../components/Turnstile';
import { useAuth } from '../context/AuthContext';

// ── Vehicle catalog from the database (single source of truth) ──
import { useVehicles } from '../hooks/useVehicles';
import {
  VEHICLE_YEARS, VEHICLE_GEARS, VEHICLE_DRIVES, VEHICLE_DOORS,
} from '../models/vehicleModel';
// ── Part catalog from the database (Kategori → Alt Kategori → Parça) ──
import { usePartCatalog } from '../hooks/usePartCatalog';
import { decodeVin } from '../services/api';
// import { CITIES } from '../models/cityModel';

const inp = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition';
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';
const STEPS = ['Araç Bilgileri', 'Parça Bilgisi', 'İletişim'];

/* ── Step indicator ── */
const StepBar = ({ current }) => (
  <div className="flex border-b border-gray-100 mb-7">
    {STEPS.map((s, i) => (
      <div key={s} className={`flex-1 py-3 text-center text-sm font-semibold border-b-2 transition
        ${i === current ? 'border-blue-700 text-blue-700' : i < current ? 'border-green-500 text-green-600' : 'border-transparent text-gray-400'}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mr-1.5
          ${i === current ? 'bg-blue-700 text-white' : i < current ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
          {i < current ? '✓' : i + 1}
        </span>
        <span className="hidden sm:inline">{s}</span>
      </div>
    ))}
  </div>
);

/* ── Sidebar ── */
const Sidebar = () => (
  <aside className="space-y-4">
    <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
      <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
        <span className="w-5 h-5 bg-blue-700 text-white rounded-full flex items-center justify-center text-xs font-bold">i</span>
        Nasıl Çalışır?
      </h4>
      <ul className="space-y-2 text-sm text-blue-700">
        <li className="flex gap-2"><span className="font-bold shrink-0">1.</span>Şasi numarasıyla aracınızı otomatik tanımlayın veya manuel doldurun.</li>
        <li className="flex gap-2"><span className="font-bold shrink-0">2.</span>Kategori, alt kategori ve parçayı seçin.</li>
        <li className="flex gap-2"><span className="font-bold shrink-0">3.</span>Talebiniz tedarikçilere anında iletilir.</li>
      </ul>
    </div>
    <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
      <h4 className="text-sm font-semibold text-gray-800 mb-3">💡 İpuçları</h4>
      <ul className="space-y-2 text-sm text-gray-500">
        <li>• Şasi numaranız ruhsat belgenizde veya ön camın alt köşesindedir.</li>
        <li>• OEM numarasını eklemek daha doğru teklif almanızı sağlar.</li>
        <li>• Motor kodu ve güç bilgisi tedarikçilerin doğru parçayı bulmasına yardımcı olur.</li>
      </ul>
    </div>
    <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex gap-3">
      <span className="text-green-600 text-lg shrink-0">🔒</span>
      <div>
        <p className="text-sm font-semibold text-green-800">Ücretsiz & Güvenli</p>
        <p className="text-xs text-green-600 mt-0.5">Talep oluşturmak tamamen ücretsizdir. Bilgileriniz 3. şahıslarla paylaşılmaz.</p>
      </div>
    </div>
  </aside>
);

export default function RequestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill  = location.state || {};
  const { user } = useAuth();
  const { brands: VEHICLE_BRANDS, models: VEHICLE_MODELS, engines: VEHICLE_ENGINES } = useVehicles();

  const [step,    setStep]    = useState(0);
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // VIN lookup
  const [vin,        setVin]        = useState('');
  const [vinLoading, setVinLoading] = useState(false);
  const [vinFound,   setVinFound]   = useState(false);

  // Vehicle form
  const [v, setVehicle] = useState({
    brand:'', model:'', year:'', fuel:'', gear:'', engine:'', doors:'', drive:'', power:'',
    ...prefill,
  });

  // Part form — now with category + subcategory + partName
  const [p, setPart] = useState({
    category: prefill.category || '',
    subcategory: prefill.subcategory || '',
    part_name: prefill.part_name || '',
    description: '',
  });

  // Contact form
  const [c, setContact] = useState({ full_name:'', phone:'', email:'' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [images, setImages] = useState([]);   // File[]

  const addImages = (fileList) => {
    const incoming = Array.from(fileList || []);
    const valid = incoming.filter(f =>
      /^image\/(jpeg|png|webp)$/.test(f.type) && f.size <= 6 * 1024 * 1024
    );
    setImages(prev => [...prev, ...valid].slice(0, 5));   // max 5
  };
  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const sv = (f, val) => setVehicle(prev => ({
    ...prev, [f]: val,
    ...(f === 'brand' ? { model:'', engine:'' } : {}),
    ...(f === 'model' ? { engine:'' } : {}),
  }));
  const sp = (f, val) => setPart(prev => ({
    ...prev, [f]: val,
    ...(f === 'category'    ? { subcategory:'', part_name:'' } : {}),
    ...(f === 'subcategory' ? { part_name:'' }                 : {}),
  }));
  const sc = (f, val) => setContact(prev => ({ ...prev, [f]: val }));

  // Derived subcategory and part lists (from the database catalog)
  const partCatalog   = usePartCatalog();
  const subcategories = p.category ? (partCatalog.subcategories[p.category] || []) : [];
  const partNames     = (p.category && p.subcategory)
    ? (partCatalog.parts[`${p.category}|${p.subcategory}`] || [])
    : [];

  // VIN lookup — decodes via the backend proxy (NHTSA), auto-fills the form.
  const lookupVin = async () => {
    const code = vin.toUpperCase().trim();
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(code)) {
      setError('Geçerli bir 17 haneli şasi numarası girin.');
      return;
    }
    setError('');
    setVinLoading(true);
    setVinFound(false);
    try {
      const res = await decodeVin(code);
      setVehicle(prev => ({ ...prev, ...res.data }));
      setVinFound(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Şasi numarası çözümlenemedi. Lütfen manuel doldurun.');
      setVinFound(false);
    } finally {
      setVinLoading(false);
    }
  };

  const nextStep = () => {
    setError('');
    if (step === 0) {
      if (!v.brand) return setError('Lütfen marka seçin.');
      if (!v.model) return setError('Lütfen model seçin.');
      if (!v.year)  return setError('Lütfen bouwjaar seçin.');
    }
    if (step === 1) {
      if (!p.category)             return setError('Lütfen parça kategorisi seçin.');
      if (!p.subcategory)          return setError('Lütfen alt kategori seçin.');
      if (!p.part_name)             return setError('Lütfen parça seçin.');
    }
    setStep(s => s + 1);
  };

  const submit = async () => {
    setError('');
    if (!c.full_name) return setError('Ad soyad zorunludur.');
    if (!c.phone)    return setError('Telefon numarası zorunludur.');
    if (!c.email)    return setError('E-posta adresi zorunludur.');
    if (captchaEnabled && !captchaToken) return setError('Lütfen güvenlik doğrulamasını tamamlayın.');
    setLoading(true);
    const res = await handleCreateRequest({ ...v, ...p, ...c }, captchaToken, images);
    setLoading(false);
    if (res.error) { setError(res.error); return; }
    setSuccess(true);
  };

  if (success) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 text-3xl">✓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Talebiniz Gönderildi!</h2>
          <p className="text-gray-400 text-sm mb-6 leading-relaxed">
            <strong className="text-gray-700">{v.brand} {v.model} ({v.year})</strong> için{' '}
            <strong className="text-blue-700">{p.part_name}</strong> talebiniz{' '}
            <strong className="text-blue-700">tedarikçilere</strong> iletildi.
            Teklifler <strong>{c.email}</strong> adresinize ulaşacak.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setSuccess(false); setStep(0); }}
              className="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-xl text-sm hover:border-gray-300 transition">
              Yeni Talep
            </button>
            <button onClick={() => {
                const role = user?.role;
                if (role === 'admin')    navigate('/dashboard/admin');
                else if (role === 'supplier') navigate('/dashboard/supplier');
                else if (role === 'customer') navigate('/dashboard/customer');
                else navigate('/');
              }}
              className="bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition">
              Ana Sayfa
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50"><Navbar />

      {/* Page header */}
      <div className="bg-white border-b border-gray-100 pt-20 sm:pt-24 pb-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <button onClick={() => navigate('/')} className="hover:text-blue-700 transition">Ana Sayfa</button>
            <span>›</span><span className="text-gray-600">Parça Talebi</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">Parça Talebi Oluştur</h1>
          <div className="flex flex-wrap gap-3 sm:gap-5 mt-2">
            {['✓ Ücretsiz', '✓ Güvenli', '✓ Tedarikçilere Anında İletilir'].map(t => (
              <span key={t} className="text-xs text-gray-500">{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid md:grid-cols-3 gap-6 sm:gap-8">
        <div className="md:col-span-2">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-7">
            <StepBar current={step} />

            {error && (
              <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm mb-5">
                ⚠️ {error}
              </div>
            )}

            {/* ── STEP 0: Vehicle ── */}
            {step === 0 && (
              <div>
                {/* VIN lookup */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                  <label className="block text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">
                    Şasi / VIN Numarası ile Otomatik Doldur
                  </label>
                  <div className="flex gap-2">
                    <input type="text"
                      className="flex-1 px-3 py-2.5 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-600 transition font-mono uppercase"
                      placeholder="Örn: 1ABCD23EFGH456789"
                      value={vin} maxLength={17}
                      onChange={e => { setVin(e.target.value.toUpperCase()); setVinFound(false); }}
                      onKeyDown={e => e.key === 'Enter' && lookupVin()}
                    />
                    <button onClick={lookupVin} disabled={vinLoading}
                      className="bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition whitespace-nowrap">
                      {vinLoading ? '...' : 'Sorgula'}
                    </button>
                  </div>
                  {vinFound && <p className="text-green-700 text-xs mt-2 font-medium">✓ Araç bilgileri otomatik dolduruldu.</p>}
                  <p className="text-blue-500 text-xs mt-1.5">Ruhsat belgenizde veya ön camın alt köşesinde bulunur.</p>
                </div>

                {/* Vehicle fields */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <div>
                    <label className={lbl}>Marka <span className="text-red-400">*</span></label>
                    <select className={inp} value={v.brand} onChange={e => sv('brand', e.target.value)}>
                      <option value="">-</option>
                      {VEHICLE_BRANDS.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Model <span className="text-red-400">*</span></label>
                    <select className={inp} value={v.model} onChange={e => sv('model', e.target.value)} disabled={!v.brand}>
                      <option value="">-</option>
                      {(VEHICLE_MODELS[v.brand] || []).map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Yıl <span className="text-red-400">*</span></label>
                    <select className={inp} value={v.year} onChange={e => sv('year', e.target.value)}>
                      <option value="">-</option>
                      {VEHICLE_YEARS.map(y => <option key={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Şasi Numarası</label>
                    <input type="text" className={`${inp} font-mono uppercase`}
                      placeholder="1ABCD23EFGH456789"
                      value={vin} maxLength={17}
                      onChange={e => setVin(e.target.value.toUpperCase())} />
                  </div>
                  <div>
                    <label className={lbl}>Motor Tipi</label>
                    <select className={inp} value={v.engine} onChange={e => sv('engine', e.target.value)}
                      disabled={!(VEHICLE_ENGINES[`${v.brand}|${v.model}`] || []).length}>
                      <option value="">{!v.model ? 'Önce marka ve model seçin' : ((VEHICLE_ENGINES[`${v.brand}|${v.model}`] || []).length ? '-' : 'Motor bilgisi yok')}</option>
                      {(VEHICLE_ENGINES[`${v.brand}|${v.model}`] || []).map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Kapı Sayısı</label>
                    <select className={inp} value={v.doors} onChange={e => sv('doors', e.target.value)}>
                      <option value="">-</option>
                      {VEHICLE_DOORS.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Çekiş Tipi</label>
                    <select className={inp} value={v.drive} onChange={e => sv('drive', e.target.value)}>
                      <option value="">-</option>
                      {VEHICLE_DRIVES.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Vites Tipi</label>
                    <select className={inp} value={v.gear} onChange={e => sv('gear', e.target.value)}>
                      <option value="">-</option>
                      {VEHICLE_GEARS.map(g => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                </div>

                {/* Vehicle preview */}
                {v.brand && v.model && v.year && (
                  <div className="mt-5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="text-2xl">🚗</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{v.brand} {v.model} — {v.year}</p>
                      <p className="text-xs text-gray-400">{[v.engine, v.gear, v.drive].filter(Boolean).join(' · ')}</p>
                    </div>
                    <span className="ml-auto text-xs text-green-600 font-semibold bg-green-50 border border-green-100 px-2.5 py-1 rounded-lg">✓ Seçildi</span>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-3">* Zorunlu alan</p>
              </div>
            )}

            {/* ── STEP 1: Part ── */}
            {step === 1 && (
              <div className="space-y-4">
                {/* Vehicle summary */}
                <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-3">
                  <span className="text-xl">🚗</span>
                  <p className="text-sm font-medium text-gray-700">{v.brand} {v.model} — {v.year}{v.engine && ` · ${v.engine}`}</p>
                </div>

                {/* Dropdown 1 — Category */}
                <div>
                  <label className={lbl}>Parça Kategorisi <span className="text-red-400">*</span></label>
                  <select className={inp} value={p.category} onChange={e => sp('category', e.target.value)}>
                    <option value="">Kategori seçin</option>
                    {partCatalog.categories.map(cat => <option key={cat}>{cat}</option>)}
                  </select>
                </div>

                {/* Dropdown 2 — Subcategory (shown when category selected) */}
                {p.category && (
                  <div>
                    <label className={lbl}>Alt Kategori <span className="text-red-400">*</span></label>
                    <select className={inp} value={p.subcategory} onChange={e => sp('subcategory', e.target.value)}>
                      <option value="">Alt kategori seçin</option>
                      {subcategories.map(sub => <option key={sub}>{sub}</option>)}
                    </select>
                  </div>
                )}

                {/* Dropdown 3 — Part name (shown when subcategory selected) */}
                {p.subcategory && (
                  <div>
                    <label className={lbl}>Parça <span className="text-red-400">*</span></label>
                    <select className={inp} value={p.part_name} onChange={e => sp('part_name', e.target.value)}>
                      <option value="">Parça seçin</option>
                      {partNames.map(pn => <option key={pn}>{pn}</option>)}
                    </select>
                  </div>
                )}

                {/* Selection breadcrumb */}
                {p.category && (
                  <div className="flex flex-wrap gap-1.5 items-center text-xs">
                    <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg">{p.category}</span>
                    {p.subcategory && <><span className="text-gray-300">›</span><span className="bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg">{p.subcategory}</span></>}
                    {p.part_name    && <><span className="text-gray-300">›</span><span className="bg-blue-700 text-white px-2.5 py-1 rounded-lg font-semibold">{p.part_name}</span></>}
                  </div>
                )}

                {/* Description textarea */}
                <div>
                  <label className={lbl}>
                    Ek Açıklama
                    <span className="normal-case font-normal text-gray-400 ml-1">(isteğe bağlı)</span>
                  </label>
                  <textarea
                    className={`${inp} min-h-[100px] resize-y`}
                    placeholder="OEM/referans numarası, orijinal mi muadil mi istediğiniz, hasarın tanımı veya ek notlar..."
                    value={p.description}
                    maxLength={1000}
                    onChange={e => sp('description', e.target.value)}
                  />
                  <p className="text-xs text-gray-300 text-right mt-1">{p.description.length}/1000</p>
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
                  💡 OEM numarasını biliyorsanız ekleyin. Tedarikçiler çok daha hızlı yanıt verir.
                </div>
              </div>
            )}

            {/* ── STEP 2: Contact ── */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm space-y-1.5">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Talep Özeti</p>
                  <p className="text-gray-700">🚗 {v.brand} {v.model} — {v.year}</p>
                  <p className="text-gray-700">🔧 {p.category} › {p.subcategory} › <strong className="text-blue-700">{p.part_name}</strong></p>
                  {p.description && <p className="text-gray-400 text-xs line-clamp-2">📝 {p.description}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Ad Soyad <span className="text-red-400">*</span></label>
                    <input type="text" className={inp} placeholder="Adınız Soyadınız"
                      value={c.full_name} maxLength={80}
                      onChange={e => sc('full_name', e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Telefon <span className="text-red-400">*</span></label>
                    <input type="tel" className={inp} placeholder="05xx xxx xx xx"
                      value={c.phone} maxLength={15}
                      onChange={e => sc('phone', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={lbl}>E-posta <span className="text-red-400">*</span></label>
                  <input type="email" className={inp} placeholder="email@adresiniz.com"
                    value={c.email} maxLength={100}
                    onChange={e => sc('email', e.target.value)} />
                </div>
                <p className="text-xs text-gray-400">* Zorunlu alan</p>
              </div>
            )}

            {/* Photo upload on the final (contact) step */}
            {step === 2 && (
              <div className="mt-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Fotoğraf ekle (isteğe bağlı)
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Parçanın veya hasarın fotoğrafı tedarikçilerin doğru teklif vermesini hızlandırır. En fazla 5 görsel · JPG/PNG/WEBP · 6 MB.
                </p>

                <div className="flex flex-wrap gap-2 mb-2">
                  {images.map((f, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(i)}
                        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 text-xs leading-none">×</button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-2xl text-gray-300 cursor-pointer hover:border-blue-400 hover:text-blue-400 transition">
                      +
                      <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
                        onChange={e => { addImages(e.target.files); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Captcha on the final (contact) step */}
            {step === 2 && <Turnstile onVerify={setCaptchaToken} />}

            {/* Navigation */}
            <div className="flex gap-3 mt-7 pt-6 border-t border-gray-100">
              {step > 0 && (
                <button onClick={() => { setStep(s => s - 1); setError(''); }}
                  className="flex-1 border border-gray-200 text-gray-500 font-semibold py-3 rounded-xl text-sm hover:border-gray-300 transition">
                  ← Geri
                </button>
              )}
              {step < 2
                ? <button onClick={nextStep}
                    className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 rounded-xl text-sm transition">
                    Devam Et →
                  </button>
                : <button onClick={submit} disabled={loading || (captchaEnabled && !captchaToken)}
                    className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition">
                    {loading ? 'Gönderiliyor...' : '✉️ Talebi Gönder — Ücretsiz'}
                  </button>
              }
            </div>
          </div>
        </div>

        <Sidebar />
      </div>
      <Footer />
    </div>
  );
}